import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { useAuthStore } from '../stores/authStore';

export interface Settings {
  notifications: {
    daily_reminders: boolean;
    achievement_alerts: boolean;
    streak_warnings: boolean;
    email_updates: boolean;
  };
  privacy: {
    profile_visibility: 'public' | 'friends' | 'private';
    show_progress: boolean;
    show_achievements: boolean;
  };
  learning: {
    difficulty_level: 'beginner' | 'intermediate' | 'advanced';
    daily_goal: number;
    auto_advance: boolean;
    pronunciation_sensitivity: number;
  };
  audio: {
    playback_speed: number;
    voice_feedback: boolean;
    microphone_sensitivity: number;
  };
}

export const defaultSettings: Settings = {
  notifications: {
    daily_reminders: true,
    achievement_alerts: true,
    streak_warnings: true,
    email_updates: false,
  },
  privacy: {
    profile_visibility: 'public',
    show_progress: true,
    show_achievements: true,
  },
  learning: {
    difficulty_level: 'beginner',
    daily_goal: 10,
    auto_advance: false,
    pronunciation_sensitivity: 0.7,
  },
  audio: {
    playback_speed: 1.0,
    voice_feedback: true,
    microphone_sensitivity: 0.5,
  },
};

const fetchSettings = async (userId: string): Promise<Settings> => {
  const { data, error } = await supabase
    .from('user_settings')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error && error.code !== 'PGRST116') {
    throw new Error(error.message);
  }

  return data?.settings ? data.settings as unknown as Settings : defaultSettings;
};

const saveSettings = async (userId: string, settings: Settings) => {
  const { data, error } = await supabase
    .from('user_settings')
    .upsert({
      user_id: userId,
      settings: settings as any,
      updated_at: new Date().toISOString(),
    })
    .select()
    .single();

  if (error) throw new Error(error.message);
  return data;
};

const deleteUserAccount = async (_userId: string) => {
  // TODO: Implement delete_user_account RPC function
  // const { error } = await supabase.rpc('delete_user_account', { user_id: userId });
  const error = null;
  if (error) throw new Error('Failed to delete user account');
  await supabase.auth.signOut();
};

export const useSettings = () => {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: ['settings', user?.id],
    queryFn: () => fetchSettings(user!.id),
    enabled: !!user,
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (newSettings: Settings) => saveSettings(user!.id, newSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', user?.id] });
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: () => deleteUserAccount(user!.id),
  });

  const updateSetting = (category: keyof Settings, key: string, value: any) => {
    if (!settingsQuery.data) return;

    const newSettings = {
      ...settingsQuery.data,
      [category]: {
        ...settingsQuery.data[category],
        [key]: value,
      },
    };

    updateSettingsMutation.mutate(newSettings);
  };

  const resetToDefaults = () => {
    updateSettingsMutation.mutate(defaultSettings);
  };

  return {
    settings: settingsQuery.data ?? defaultSettings,
    isLoading: settingsQuery.isLoading,
    isSaving: updateSettingsMutation.isPending,
    isDeleting: deleteAccountMutation.isPending,
    updateSetting,
    resetToDefaults,
    deleteAccount: deleteAccountMutation.mutate,
    error: settingsQuery.error || updateSettingsMutation.error || deleteAccountMutation.error,
  };
};